import Phaser from "phaser";
import {Stats} from "./Stats";
import {GameConfig} from "../config/GameConfig";
import {createDissolveEffect} from "./Dissolve";
import {damageColor} from "./DamageColor";
import {uiBorderColor} from "./Colors";
import {attackFrameInterval, defaultFrameInterval} from "./Timings";

export class Player extends Phaser.Physics.Arcade.Sprite {
    public static initFrame = 'CHARACTER idle1'
    public static idleFrames: string[] = ['CHARACTER idle1', 'CHARACTER idle2', 'CHARACTER idle3', 'CHARACTER idle4',
        'CHARACTER idle5', 'CHARACTER idle6', 'CHARACTER idle7', 'CHARACTER idle8']
    public static moveFrames: string[] = ['CHARACTER walk1', 'CHARACTER walk2', 'CHARACTER walk3', 'CHARACTER walk4',
        'CHARACTER walk5', 'CHARACTER walk6', 'CHARACTER walk7', 'CHARACTER walk8'];
    public static attackFrames: string[] = ['CHARACTER Attack0',
        'CHARACTER Attack1', 'CHARACTER Attack2', 'CHARACTER Attack3', 'CHARACTER Attack4',
        'CHARACTER Attack5', 'CHARACTER Attack6', 'CHARACTER Attack7', 'CHARACTER Attack8']
    private currentFrameIndex: number = 0;
    private lastTime: number = 0;
    private lastFrameTime: number = 0;
    private isMoving: boolean = false;
    private isAttacking: boolean = false;
    private isDamaging: boolean = false;
    private boostingColor: number | undefined = undefined
    private stats!: Stats
    private particleContainer: Phaser.GameObjects.Container;
    private healthBox: Phaser.GameObjects.Graphics
    private boxWidth: number = 32
    private boxHeight: number = 2
    private lastStepTime: number = 0;
    private stepSoundInterval: number = 300;
    private stepSounds: string[] = ['sound_step_1', 'sound_step_2', 'sound_step_3', 'sound_step_4'];
    private glowFX?: Phaser.FX.Glow

    constructor(scene: Phaser.Scene, x: number, y: number, particleContainer: Phaser.GameObjects.Container) {
        super(scene, x, y, 'player');
        scene.add.existing(this);
        scene.physics.add.existing(this);
        this.setTexture(Player.moveFrames[0]);
        this.setDepth(10000);

        this.stats = this.scene.registry.get('playerStats') as Stats ?? GameConfig.player.startStats
        this.particleContainer = particleContainer

        this.healthBox = scene.add.graphics();
        this.healthBox.setScrollFactor(0);
        this.healthBox.setDepth(1000);
        this.glowFX = this.preFX?.addGlow();
        this.glowFX?.setActive(false)
    }

    private drawHealth() {
        const { width, height } = this.scene.scale;

        this.healthBox.clear();

        const boxX = 84
        const boxY = 116

        this.healthBox.lineStyle(2, uiBorderColor);
        this.healthBox.strokeRect(boxX, boxY, this.boxWidth, this.boxHeight);

        this.healthBox.fillStyle(0x00ff00);
        const playerFillWidth = (Math.max(this.stats.health, 0.0) / this.stats.maxHealth) * this.boxWidth;
        this.healthBox.fillRect(boxX, boxY, playerFillWidth, this.boxHeight);
    }

    private playRandomStepSound(): void {
        const randomIndex = Math.floor(Math.random() * this.stepSounds.length);
        this.scene.sound.play(this.stepSounds[randomIndex], {
            volume: 0.45
        });
    }

    startMoving(): void {
        if (!this.isMoving) {
            this.isMoving = true;
            this.currentFrameIndex = 1;
            this.setTexture(Player.moveFrames[this.currentFrameIndex]);
        }
    }

    stopMoving(): void {
        if (this.isMoving) {
            this.isMoving = false;
            this.currentFrameIndex = 0;
            this.setTexture(Player.initFrame);
        }
    }

    startAttacking(): void {
        if (!this.isAttacking) {
            this.isAttacking = true
            this.currentFrameIndex = 1;
            this.setTexture(Player.attackFrames[this.currentFrameIndex]);
            this.lastFrameTime = this.lastTime
        }

        this.scene.sound.play('sound_attack_player', {
            loop: false,
            volume: 0.5
        })
    }

    stopAttacking(): void {
        if (this.isAttacking) {
            this.isAttacking = false;
            this.currentFrameIndex = 0;
            this.setTexture(Player.initFrame);
        }
    }

    startDamaging(level: number = 0): void {
        if (!this.isDamaging) {
            this.isDamaging = true;
            this.setTint(damageColor);

            this.scene?.time.delayedCall(100, () => {
                this.stopDamaging()

                if (level < 2) {
                    this.scene?.time.delayedCall(100, () => {
                        this.startDamaging(level + 1)
                    })
                }
            })
        }
    }

    stopDamaging(): void {
        if (this.isDamaging) {
            this.isDamaging = false;
            this.setTint(0xffffff);
        }
    }

    startBoosting(boostingColor: number): void {
        if (this.boostingColor === undefined) {
            this.boostingColor = boostingColor;
            this.setTint(boostingColor);

            if (this.glowFX) {
                this.glowFX.setActive(true)
                this.glowFX.outerStrength = 2
                this.glowFX.color = boostingColor
            }

            this.scene?.time.delayedCall(1000, () => {
                this.stopBoosting()
            })
        }
    }

    stopBoosting(): void {
        if (this.boostingColor !== undefined) {
            this.boostingColor = undefined;
            this.setTint(0xffffff);

            if (this.glowFX) {
                this.glowFX.setActive(false);
            }
        }
    }

    update(time: number, delta: number): void {
        let targetFrames: string[]

        if (this.isAttacking) {
            targetFrames = Player.attackFrames
        }
        else if (this.isMoving) {
            targetFrames = Player.moveFrames
        }
        else {
            targetFrames = Player.idleFrames
        }

        if (time - this.lastFrameTime > (this.isAttacking ? attackFrameInterval : defaultFrameInterval)) {
            this.lastFrameTime = time;
            this.currentFrameIndex = (++this.currentFrameIndex) % targetFrames.length;
            this.setTexture(targetFrames[this.currentFrameIndex]);

            if (this.isAttacking && this.currentFrameIndex === 0) {
                this.stopAttacking()
            }
        }

        if (this.isMoving && time - this.lastStepTime > this.stepSoundInterval) {
            this.playRandomStepSound();
            this.lastStepTime = time;
        }

        if (this.isDamaging) {
            this.setTint(damageColor);
        }
        else if (this.boostingColor !== undefined) {
            this.setTint(this.boostingColor);
        } else {
            this.setTint(0xffffff);
        }

        this.lastTime = time
        this.drawHealth()
    }

    public getStats(): Stats {
        return this.stats;
    }

    dissolve() {
        createDissolveEffect(this, this.particleContainer)
    }

    hide() {
        this.visible = false
        this.healthBox.visible = false
    }
}
